import React from 'react';
import { Chip } from '@mui/material';
import { getFirstCaseInsensitiveValue } from '../../../components/detailPanel/appsheet/appsheetFieldUtils';
import { multiSelectFilterFn } from '../appsheetAccountsBrowser/fuzzySearch';

const normalizeType = (value) => String(value ?? '').trim().toLowerCase();
const normalizeLooseKey = (value) => String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');

const getLooseValue = (row, candidates = []) => {
  if (!row || typeof row !== 'object') return undefined;
  const targetSet = new Set(candidates.map((candidate) => normalizeLooseKey(candidate)).filter(Boolean));
  if (!targetSet.size) return undefined;
  for (const [key, value] of Object.entries(row)) {
    if (targetSet.has(normalizeLooseKey(key))) return value;
  }
  return undefined;
};

export const loadJson = (key, fallback) => {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (_error) {
    return fallback;
  }
};

export const saveJson = (key, value) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (_error) {
    // Ignore storage errors.
  }
};

export const buildColumns = (fields) =>
  (fields || []).map((field) => {
    const normalizedType = normalizeType(field?.dataType);
    const isEnum = normalizedType === 'enum' || normalizedType === 'enumlist';
    const options = Array.isArray(field?.enumValues)
      ? field.enumValues.map((value) => String(value)).filter(Boolean)
      : [];

    return {
      id: field.name,
      header: field.displayName || field.name,
      size: 190,
      accessorFn: (row) => row?.[field.name],
      ...(isEnum
        ? {
            filterVariant: 'multi-select',
            filterFn: multiSelectFilterFn,
            filterSelectOptions: options,
          }
        : {}),
      Cell: ({ cell }) => {
        const value = cell.getValue();
        if (value === null || value === undefined || value === '') return '';
        if (typeof value === 'boolean') return <Chip size="small" label={value ? 'Yes' : 'No'} variant="outlined" />;
        if (Array.isArray(value)) return value.join(', ');
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
      },
    };
  });

export const getOrderItemAccountName = (row) =>
  String(
    getFirstCaseInsensitiveValue(row, ['Account_Name__c', 'account_name__c', 'AccountName', 'accountName', 'Account Name', 'Name']) ||
      getLooseValue(row, ['accountnamec', 'accountname']) ||
      ''
  ).trim();

export const getOrderItemBac = (row) =>
  String(
    getFirstCaseInsensitiveValue(row, [
      'Dealer BAC',
      'Dealer_BAC',
      'DealerBAC',
      'dealer_bac',
      'dealer bac',
      'dealerBAC',
      'BAC_Code__c',
      'bac_code__c',
      'BAC__c',
      'bac__c',
      'BAC',
      'bac',
      'BAC Code',
      'BAC_Code',
      'BacCode',
      'BACCode',
      'Dealer_BAC__c',
      'dealer_bac__c',
      'DealerBAC',
    ]) ||
      getLooseValue(row, ['baccodec', 'baccode', 'dealerbacc', 'dealerbac']) ||
      ''
  ).trim();

export const getOrderItemId = (row) =>
  String(getFirstCaseInsensitiveValue(row, ['Id', 'id', 'OrderItemId', 'orderItemId', 'order_item_id']) || '').trim();

export const getOrderItemDiBrandName = (row) =>
  String(
    getFirstCaseInsensitiveValue(row, ['DI_Brand_Name__c', 'di_brand_name__c', 'DI Brand Name', 'diBrandName']) || ''
  ).trim();

export const getOrderItemAccountId = (row) =>
  String(
    getFirstCaseInsensitiveValue(row, ['Account_ID__c', 'account_id__c', 'AccountId', 'accountId', 'account_id', 'Account ID']) ||
      getLooseValue(row, ['accountidc', 'accountid']) ||
      ''
  ).trim();

export const sourceSnapshotMarker = (table) => {
  if (table === undefined) return undefined;
  return {
    provider: table?.sourceProvider ?? 'appsheet',
    generationId: table?.sourceGenerationId ?? null,
    contractVersion: table?.sourceContractVersion ?? null,
    snapshotRunId: table?.sourceSnapshotRunId ?? null,
  };
};

export const pairedSourceGenerationState = (accountsMarker, orderItemsMarker) => {
  if (accountsMarker === undefined || orderItemsMarker === undefined) return 'pending';
  const validMarker = (marker) => {
    if (marker?.provider === 'appsheet') {
      return marker.generationId === null && marker.contractVersion === null && marker.snapshotRunId === null;
    }
    if (marker?.provider === 'hostinger') {
      return marker?.provider === 'hostinger' &&
      typeof marker.generationId === 'string' && marker.generationId.length > 0 &&
      marker.contractVersion === '1' &&
      typeof marker.snapshotRunId === 'string' && marker.snapshotRunId.length > 0;
    }
    return false;
  };
  if (!validMarker(accountsMarker) || !validMarker(orderItemsMarker)) return 'mismatch';
  return accountsMarker?.provider === orderItemsMarker?.provider &&
    accountsMarker?.generationId === orderItemsMarker?.generationId &&
    accountsMarker?.contractVersion === orderItemsMarker?.contractVersion &&
    accountsMarker?.snapshotRunId === orderItemsMarker?.snapshotRunId
    ? 'match'
    : 'mismatch';
};

export const rowsForPairedGeneration = (generationState, rows) =>
  generationState === 'match' ? rows : [];

export const getDefaultPinning = (fields = []) => {
  const candidates = ['Id', 'Account_Name__c', 'BAC_Code__c', 'DI_Brand_Name__c', 'Account_ID__c'];
  const available = new Set(fields.map((field) => field.name));
  return {
    left: candidates.filter((name) => available.has(name)),
    right: [],
  };
};
